import type {
  BuildingKey,
  KnightLevelStats,
  ResourceAmounts,
  TrinketTier,
  TroopClass,
  TroopDefinition,
  TroopTier,
  WeaponStats,
} from "./types";

const resources = (
  food = 0,
  wood = 0,
  stone = 0,
  gold = 0,
): ResourceAmounts => ({ food, wood, stone, gold });

export const KNIGHT_LEVEL_TABLE: readonly KnightLevelStats[] = [
  [1, 3480, 3350, 6480, 350], [2, 3610, 3470, 6720, 380],
  [3, 3740, 3590, 6950, 460], [4, 3860, 3720, 7190, 580],
  [5, 3990, 3840, 7430, 720], [6, 4120, 3960, 7660, 890],
  [7, 4250, 4080, 7900, 1080], [8, 4370, 4210, 8140, 1300],
  [9, 4500, 4330, 8370, 1540], [10, 4630, 4450, 8610, 1810],
  [11, 4750, 4570, 8850, 2090], [12, 4880, 4690, 9080, 2400],
  [13, 5010, 4820, 9320, 2730], [14, 5140, 4940, 9560, 3070],
  [15, 5260, 5060, 9790, 3440], [16, 5390, 5180, 10030, 3820],
  [17, 5520, 5310, 10270, 4230], [18, 5640, 5430, 10500, 4650],
  [19, 5770, 5550, 10740, 5080], [20, 5900, 5670, 10980, 5540],
  [21, 6030, 5790, 11210, 6010], [22, 6150, 5920, 11450, 6500],
  [23, 6280, 6040, 11690, 7010], [24, 6410, 6160, 11920, 7530],
  [25, 6530, 6280, 12160, 8070], [26, 6660, 6410, 12400, 8630],
  [27, 6790, 6530, 12630, 9200], [28, 6920, 6650, 12870, 9780],
  [29, 7040, 6770, 13110, 10380], [30, 7170, 6890, 13340, 11000],
].map(([level, attack, defence, hp, power]) => ({ level, attack, defence, hp, power }));

export const WEAPON_LEVEL_TABLE: readonly (WeaponStats & { requiredLevel: number })[] = [
  [1, 400, 350, 190], [2, 700, 600, 270], [3, 1000, 900, 420],
  [4, 1300, 1150, 650], [5, 1550, 1400, 940], [6, 1850, 1700, 1280],
  [7, 2150, 1950, 1680], [8, 2450, 2200, 2120], [9, 2750, 2500, 2620],
  [10, 3050, 2750, 3150], [11, 3350, 3000, 3730], [12, 3600, 3300, 4350],
  [13, 3900, 3550, 5020], [14, 4200, 3850, 5720], [15, 4500, 4100, 6460],
  [16, 4800, 4350, 7230], [17, 5100, 4650, 8060], [18, 5400, 4900, 8910],
  [19, 5700, 5200, 9800], [20, 6000, 5400, 10730], [21, 6250, 5700, 11690],
  [22, 6550, 5950, 12670], [23, 6850, 6250, 13720], [24, 7150, 6500, 14760],
  [25, 7450, 6750, 15850], [26, 7750, 7050, 16980], [27, 8000, 7300, 18150],
  [28, 8300, 7550, 19330], [29, 8600, 7850, 20540], [30, 8900, 8100, 21800],
].map(([requiredLevel, attack, defence, power]) => ({ requiredLevel, attack, defence, power }));

export const LEGENDARY_LEVEL_30_WEAPON: WeaponStats & { requiredLevel: 30 } = {
  requiredLevel: 30,
  attack: 9350,
  defence: 8500,
  power: 22890,
};

export const TRINKET_TIER_RANGES: Readonly<Record<TrinketTier, {
  castleMin: number;
  castleMax: number;
  balanced: { attackPct: readonly [number, number]; defencePct: readonly [number, number]; hpPct: readonly [number, number] };
}>> = {
  1: { castleMin: 1, castleMax: 4, balanced: { attackPct: [2, 4], defencePct: [2, 4], hpPct: [1, 3] } },
  2: { castleMin: 5, castleMax: 8, balanced: { attackPct: [2, 4], defencePct: [3, 5], hpPct: [4, 6] } },
  3: { castleMin: 9, castleMax: 12, balanced: { attackPct: [4, 6], defencePct: [4, 6], hpPct: [3, 5] } },
  4: { castleMin: 13, castleMax: 16, balanced: { attackPct: [5, 7], defencePct: [5, 7], hpPct: [4, 6] } },
  5: { castleMin: 17, castleMax: 20, balanced: { attackPct: [5, 7], defencePct: [6, 8], hpPct: [7, 9] } },
};

const troop = (
  troopClass: TroopClass,
  tier: TroopTier,
  combatPower: number,
  attack: number,
  defence: number,
  hp: number,
  load: number,
  foodUpkeepPerHour: number,
  trainSeconds: number,
  trainCost: ResourceAmounts,
  healCost: ResourceAmounts,
): TroopDefinition => ({
  troopClass,
  tier,
  combatPower,
  kingdomPower: tier,
  attack,
  defence,
  hp,
  load,
  foodUpkeepPerHour,
  trainSeconds,
  trainCost,
  healCost,
});

export const TROOP_DEFINITIONS: readonly TroopDefinition[] = [
  troop("infantry", 1, 4, 1, 4, 4, 14, 0.06, 5, resources(16), resources(8)),
  troop("infantry", 2, 9, 2, 7, 6, 22, 0.22, 11, resources(37, 12, 1), resources(18, 6, 1)),
  troop("infantry", 3, 20, 4, 10, 9, 28, 0.39, 24, resources(105, 26, 4, 1), resources(52, 13, 2, 1)),
  troop("infantry", 4, 44, 6, 16, 14, 36, 0.55, 54, resources(178, 82, 12, 2), resources(89, 41, 6, 1)),
  troop("cavalry", 1, 4, 2, 2, 1, 11, 0.06, 5, resources(15), resources(7)),
  troop("cavalry", 2, 9, 4, 3, 2, 16, 0.22, 11, resources(39, 10, 1), resources(20, 5, 1)),
  troop("cavalry", 3, 20, 5, 5, 3, 19, 0.39, 24, resources(96, 26, 3, 1), resources(48, 13, 2, 1)),
  troop("cavalry", 4, 44, 8, 8, 5, 25, 0.55, 54, resources(179, 78, 12, 2), resources(90, 39, 6, 1)),
  troop("archers", 1, 4, 2, 1, 1, 14, 0.06, 5, resources(16), resources(8)),
  troop("archers", 2, 9, 4, 2, 2, 19, 0.22, 11, resources(35, 13, 1), resources(18, 6, 1)),
  troop("archers", 3, 20, 6, 3, 3, 25, 0.39, 24, resources(100, 28, 3, 1), resources(50, 14, 2, 1)),
  troop("archers", 4, 44, 9, 5, 5, 33, 0.55, 54, resources(175, 85, 12, 2), resources(87, 43, 6, 1)),
];

export const TROOP_TIER_UNLOCK_LEVEL: Readonly<Record<TroopTier, number>> = {
  1: 1,
  2: 7,
  3: 13,
  4: 20,
};

export const MARCH_CAPACITY_BY_CASTLE_LEVEL = [
  500, 750, 1000, 1300, 1700, 2100, 2600, 3200, 3900, 4700,
  5600, 6600, 7700, 8900, 10200, 11600, 13100, 14800, 16700, 20000,
] as const;

export const BUILDING_KEYS: readonly BuildingKey[] = [
  "main_castle", "farm", "lumbermill", "quarry", "gold_mine", "treasury",
  "barracks", "archery_range", "stable", "infirmary",
];

export type BuildingPowerRow = {
  level: number;
  mainCastle: number;
  eachResource: number;
  treasury: number;
  eachMilitary: number;
  infirmary: number;
  allTen: number;
};

export const BUILDING_POWER_BY_LEVEL: readonly BuildingPowerRow[] = [
  [1,200,50,50,50,50,650],[2,260,60,70,70,70,850],[3,390,90,100,120,120,1330],
  [4,550,120,150,180,190,1910],[5,760,160,200,260,270,2650],[6,1000,210,270,350,360,3520],
  [7,1280,260,340,450,470,4480],[8,1580,320,420,570,590,5580],[9,1900,390,510,690,710,6750],
  [10,2260,460,610,820,850,8020],[11,2640,530,710,960,1000,9350],[12,3040,610,820,1110,1160,10790],
  [13,3460,700,940,1270,1320,12330],[14,3910,790,1060,1440,1490,13940],[15,4370,880,1180,1610,1680,15580],
  [16,4860,970,1320,1800,1870,17330],[17,5370,1080,1460,1990,2060,19180],[18,5890,1180,1600,2180,2270,21020],
  [19,6440,1290,1750,2390,2480,23000],[20,7000,1400,1900,2600,2700,25000],
].map(([level, mainCastle, eachResource, treasury, eachMilitary, infirmary, allTen]) => ({
  level, mainCastle, eachResource, treasury, eachMilitary, infirmary, allTen,
}));

export type UpgradeRow = {
  level: number;
  cost: ResourceAmounts;
  seconds: number;
};

const mainCastleUpgradeRows = [
  [1,resources(),0],[2,resources(0,180),6],[3,resources(0,805),60],[4,resources(0,1800,360),180],
  [5,resources(0,7600,1500),600],[6,resources(0,19000,3800,960),1800],[7,resources(0,69000,13000,3400),3600],
  [8,resources(0,120000,25000,6300),9000],[9,resources(0,260000,52000,13000),16200],[10,resources(0,460000,92000,23000),21600],
  [11,resources(1300000,1300000,260000,65000),27000],[12,resources(1600000,1600000,330000,84000),32400],
  [13,resources(2300000,2300000,470000,110000),39600],[14,resources(3100000,3100000,630000,150000),50400],
  [15,resources(4600000,4600000,930000,230000),64800],[16,resources(5900000,5900000,1100000,290000),109680],
  [17,resources(9300000,9300000,1800000,460000),131640],[18,resources(12000000,12000000,2500000,620000),157980],
  [19,resources(15000000,15000000,3100000,780000),237000],[20,resources(21000000,21000000,4300000,1000000),296280],
] satisfies ReadonlyArray<readonly [number, ResourceAmounts, number]>;

export const MAIN_CASTLE_UPGRADES: readonly UpgradeRow[] = mainCastleUpgradeRows.map(
  ([level, cost, seconds]) => ({ level, cost, seconds }),
);

export type MilitaryBuildingRow = UpgradeRow & {
  unlockTier: TroopTier | null;
  batchCapacity: number;
  trainingSpeedPct: number;
};

const militaryRaw: ReadonlyArray<readonly [number, TroopTier | null, number, number, ResourceAmounts, number]> = [
  [1,1,60,0.3,resources(),2],[2,null,80,0.5,resources(0,140),9],[3,null,95,0.8,resources(0,645),45],
  [4,null,110,1.0,resources(0,1400,285),135],[5,null,125,1.3,resources(0,6000,1200),270],[6,null,140,1.6,resources(0,15000,3000,765),540],
  [7,2,155,1.8,resources(0,55000,11000,2700),1080],[8,null,175,2.1,resources(0,100000,20000,5000),1620],
  [9,null,190,2.4,resources(0,200000,41000,10000),2430],[10,null,205,2.6,resources(0,360000,73000,18000),3240],
  [11,null,220,2.9,resources(460000,460000,92000,23000),4050],[12,null,235,3.2,resources(580000,580000,110000,29000),4860],
  [13,3,250,3.4,resources(830000,830000,160000,41000),5940],[14,null,275,3.7,resources(1100000,1100000,220000,55000),7560],
  [15,null,305,4.0,resources(1600000,1600000,320000,81000),9720],[16,null,335,4.2,resources(2000000,2000000,410000,100000),16440],
  [17,null,365,4.5,resources(3200000,3200000,650000,160000),19740],[18,null,395,4.8,resources(4300000,4300000,870000,210000),23700],
  [19,null,425,5.0,resources(5400000,5400000,1000000,270000),35550],[20,4,460,5.3,resources(7500000,7500000,1500000,370000),44430],
];

export const MILITARY_BUILDING_LEVELS: readonly MilitaryBuildingRow[] = militaryRaw.map(
  ([level, unlockTier, batchCapacity, trainingSpeedPct, cost, seconds]) => ({
    level, unlockTier, batchCapacity, trainingSpeedPct, cost, seconds,
  }),
);

const infirmaryUpgradeRows = [
  [1,resources(),2],[2,resources(0,100),9],[3,resources(0,460),40],[4,resources(0,1000,205),125],
  [5,resources(0,4300,865),250],[6,resources(0,10000,2100,545),500],[7,resources(0,39000,7800,1900),990],
  [8,resources(0,72000,14000,3600),1500],[9,resources(0,140000,29000,7400),1650],[10,resources(0,260000,52000,13000),3000],
  [11,resources(320000,320000,65000,16000),3780],[12,resources(420000,420000,54000,21000),4530],
  [13,resources(590000,590000,110000,29000),5520],[14,resources(780000,780000,150000,39000),7050],
  [15,resources(1100000,1100000,230000,58000),9060],[16,resources(1400000,1400000,290000,74000),15360],
  [17,resources(2300000,2300000,460000,110000),18420],[18,resources(3100000,3100000,620000,150000),22110],
  [19,resources(3900000,3900000,780000,190000),33180],[20,resources(5300000,5300000,1000000,260000),41460],
] satisfies ReadonlyArray<readonly [number, ResourceAmounts, number]>;

export const INFIRMARY_UPGRADES: readonly (UpgradeRow & { woundedCapacity: number })[] = infirmaryUpgradeRows.map(
  ([level, cost, seconds], index) => ({ level, cost, seconds, woundedCapacity: MARCH_CAPACITY_BY_CASTLE_LEVEL[index] }),
);
